import React from "react";

import { View, StyleSheet, Platform, TouchableOpacity } from "react-native";
import { LineChart, Grid, XAxis, YAxis } from "react-native-svg-charts";
import { Circle, Line, G } from "react-native-svg";
import * as shape from "d3-shape";

import moment from "moment";
import { findIndex } from "lodash";

import MoodsHelper, { getMoodById } from "../../common/mood/MoodsHelper";
import Icons from "../../common/icons/index";
import Utils, { sortBy } from "../../../../helpers/utils";
import AppTheme from "../../AppTheme";

export default function EditReflectionLineChart({
  reflection,
  controller,
  reflectionHours = {},
  showToggleChartMenu
}) {
  const chartHeight = reflection.edit_object === "time" ? 0 : 200;
  const MAX_COUNT = 10;
  const labels = [];
  const hours = [];
  const moods = [];
  let hmax = 0;
  let hmin = -1;

  const dateFormat =
    reflection.edit_time_unit == "year"
      ? "YYYY"
      : reflection.edit_time_unit === "month" ||
        reflection.edit_time_unit === "quarter"
      ? "MMM"
      : "M/D";

  const selectedDay = moment(reflection.edit_day).format(dateFormat);
  const allDays = sortBy(Object.keys(reflectionHours));

  // Current selected gem
  const selectedBubbleIndex = findIndex(
    allDays,
    (d) => d == reflection.edit_day,
  );
  if (selectedBubbleIndex == -1) return null;

  const currentPage = Math.floor(selectedBubbleIndex / MAX_COUNT) + 1;

  const end = Math.min(allDays.length, currentPage * MAX_COUNT);
  const start = Math.max(0, end - 10);
  const chartDays = allDays.slice(start, end).reverse();

  const GRAY_MOOD = MoodsHelper.UNSET_MOOD.color;

  function adjustProjection(h) {
    try {
      if (hours.length == 0 && chartDays.length > 1) {
        const data1 = reflectionHours[chartDays[0]];
        const data2 = reflectionHours[chartDays[1]];
        const interval = moment(data1.date).diff(moment(data2.date), "hours");
        const now = Utils.nowUserTz(controller);
        const end = moment(data1.date).add(interval, "hours");
        if (end.isAfter(now)) {
          const delta = moment(end).diff(now, "hours");
          if (delta > 0) {
            let sampleHours = 0;
            let sampleDays = 0;
            for (var i = 1; i <= Math.min(5, chartDays.length); i++) {
              const d = reflectionHours[chartDays[i]];
              sampleHours += d.hours;
              sampleDays += 1;
            }
            const projection =
              (1.0 * delta * sampleHours) / (interval * sampleDays);
            return h + projection;
          }
        }
      }
    } catch (e) {
      //ignore
    }
    return h;
  }

  let hadSelection = false;
  chartDays.forEach((key) => {
    const data = reflectionHours[key];
    const day = moment(data.date).format(dateFormat);

    // Labels
    labels.unshift(day);

    // Hours
    let h = 1 * data.hours;
    h = adjustProjection(h);

    if (h > hmax) hmax = h;
    if (hmin < 0 || h < hmin) hmin = h;
    hours.unshift(h);

    // Moods
    const selected = !hadSelection && selectedDay == day;
    if (selected) hadSelection = true;
    const hasMood = data.gem && data.gem.mood;
    moods.unshift({
      hasMood,
      color: hasMood ? getMoodById(data.gem.mood).color : GRAY_MOOD,
      day,
      selected: selected,
    });
  });

  hmax = hmax + (hmax - hmin) * 0.1;
  hmin = Math.max(0, hmin - (hmax - hmin) * 0.1);

  const axesSvg = {
    fontFamily: Platform.select({
      default: "Montserrat",
      android: "Montserrat-SemiBold",
    }),
    fontWeight: "600",
    fontSize: 9,
    lineHeight: 11,
    letterSpacing: 0.175,
    fill: AppTheme.almostBlack,
  };

  const contentInset = {
    left: labels.length > MAX_COUNT ? -15 : 20,
    right: 20,
    top: 20,
    bottom: 20,
  };

  const xAxisHeight = 30;

  // ------------------------------------- METHODS -------------------------------------
  const Bubble = ({ mood, cx, cy, index }) => {
    return (
      <G key={() => "c1" + index}>
        <Circle
          cx={cx}
          cy={cy}
          r={8}
          stroke={mood.color}
          strokeWidth={6}
          fill={mood.color}
          opacity={mood.hasMood ? (mood.selected ? 1 : 0.5) : 0}
        />
        <Circle
          cx={cx}
          cy={cy}
          r={6}
          stroke={mood.color}
          fill={mood.color}
          fill={mood.selected ? "#FFF" : mood.color}
          opacity={mood.hasMood ? (mood.selected ? 0.5 : 1) : 0}
        />
      </G>
    );
  };

  const Decorator = ({ x, y, data }) => {
    return data.map((value, index) =>
      moods[index].selected ? (
        <G onPress={() => onGemSelected(data)}>
          <Line
            x1={x(index)}
            x2={x(index)}
            y1={25}
            y2={180}
            stroke={AppTheme.DEFAULT_COLOR}
            strokeWidth="2"
            strokeLinecap="round"
            strokeDasharray="1, 6"
          />
          {moods[index].hasMood ? (
            <Bubble
              index={index}
              cx={x(index)}
              cy={y(value)}
              mood={moods[index]}
            />
          ) : (
            <Icons.QuestionMarkIcon x={x(index)} y={y(value)} />
          )}
        </G>
      ) : (
        <Bubble index={index} cx={x(index)} cy={y(value)} mood={moods[index]} />
      ),
    );
  };

  const onGemSelected = (data) => {
    console.log("------------------->", data);
  };

  return (
    <>
      {chartHeight > 0 ? (
        <TouchableOpacity
          onLongPress={showToggleChartMenu}
          style={[
            styles.chart,
            {
              height: chartHeight,
            },
          ]}
        >
          <YAxis
            data={hours}
            style={[{ height: chartHeight }, { marginBottom: xAxisHeight }]}
            contentInset={contentInset}
            numberOfTicks={4}
            formatLabel={(value) => `${value}`}
            svg={axesSvg}
            min={hmin}
            max={hmax}
          />
          <View style={{ flex: 1, marginLeft: 10 }}>
            <LineChart
              style={{ height: chartHeight }}
              data={hours}
              contentInset={contentInset}
              curve={shape.curveNatural}
              svg={{ strokeWidth: 3.5, stroke: GRAY_MOOD }}
              numberOfTicks={4}
              gridMin={hmin}
              gridMax={hmax}
            >
              <Grid
                svg={{
                  stroke: GRAY_MOOD,
                }}
              />
              <Decorator />
            </LineChart>
            <XAxis
              style={{
                marginHorizontal: -10,
                height: xAxisHeight,
              }}
              data={hours}
              contentInset={contentInset}
              svg={axesSvg}
              formatLabel={(index) => labels[index]}
            />
          </View>
        </TouchableOpacity>
      ) : null}
    </>
  );
}

const styles = StyleSheet.create({
  chart: {
    padding: 2,
    flexDirection: "row",
    transform: [{ translateY: -15 }],
  },
});
